import React, { useState, useContext } from 'react';
import { Card, Alert, Button, Space, Tag, Descriptions, Result, Typography } from 'antd';
import { ExperimentOutlined, ShoppingCartOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import QrScanner from '../../components/qr/QrScanner';
import api from '../../services/api';
import { showErrorMessage } from '../../services/messages';
import EUserTypes from '../../enums/EUserTypes';
import EDrumStatus from '../../enums/EDrumStatus';
import EDrumPurchaseStatus from '../../enums/EDrumPurchaseStatus';
import GeneralContext from '../../context/GeneralContext/GeneralContext';
import { BackToHome, useIsOperator } from '../../components/drum/WizardParts';
import styles from '../../services/styles';

const { COLORS } = styles;
const { Title, Text } = Typography;

/**
 * Tela de entrada por QR code para os fluxos operacionais.
 *
 * Fluxo direcionado (PWA): ao vir da Home do operador, recebe `location.state.flow`:
 *  - 'conference': após scan, se o usuário pode conferir e o tambor está pendente,
 *    navega automaticamente para `/drums/:id/conference`.
 *  - 'process': após scan, se o usuário pode processar, navega automaticamente
 *    para `/drums/:id/process`.
 *  - Sem flow (ex.: admin acessando diretamente): mostra o resumo + botões de ação.
 *
 * Botão "Voltar" leva para /home quando o usuário é operador.
 */
const DrumEntry = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isOperator = useIsOperator();
  const { loggedUser } = useContext(GeneralContext);

  const [drum, setDrum] = useState(null);
  const [error, setError] = useState(null);

  const flow = location.state?.flow;
  const flowColor = location.state?.color;

  const canConference = [EUserTypes.PANEL_ADMIN.code, EUserTypes.OPERADOR_COMPRADOR.code].includes(loggedUser?.type);
  const canProcess = [EUserTypes.PANEL_ADMIN.code, EUserTypes.OPERADOR.code, EUserTypes.OPERADOR_COMPRADOR.code].includes(loggedUser?.type);

  const handleScan = async (uid) => {
    setError(null);
    setDrum(null);
    try {
      const response = await api.drum.getByUid(uid);
      const found = response.data;

      // Fluxo direcionado (PWA): auto-navega conforme a origem da Home,
      // repassando a cor do fluxo para a tela seguinte herdar o destaque.
      if (flow === 'conference' && canConference) {
        // Tambor já conferido → view só-leitura (não refaz o fluxo).
        if (found.purchase_status === EDrumPurchaseStatus.CONFERRED.code) {
          navigate(`/drums/${found.id}/view`, { state: { color: flowColor } });
          return;
        }
        if (found.purchase_status === EDrumPurchaseStatus.PENDING.code) {
          navigate(`/drums/${found.id}/conference`, { state: { from: 'home', color: flowColor } });
          return;
        }
      }
      if (flow === 'process' && canProcess) {
        // Tambor já processado → view só-leitura (não refaz o fluxo).
        if (found.status === EDrumStatus.PROCESSED.code) {
          navigate(`/drums/${found.id}/view`, { state: { color: flowColor } });
          return;
        }
        navigate(`/drums/${found.id}/process`, { state: { from: 'home', color: flowColor, machine: location.state?.machine } });
        return;
      }

      // Sem fluxo direcionado casando, mas o tambor já está em estado terminal
      // (processado ou conferido) → mostra direto a visualização só-leitura com
      // todos os dados, sem exigir clique em "Visualizar tambor".
      if (found.status === EDrumStatus.PROCESSED.code || found.purchase_status === EDrumPurchaseStatus.CONFERRED.code) {
        navigate(`/drums/${found.id}/view`, { state: { color: flowColor } });
        return;
      }

      setDrum(found);
    } catch (err) {
      const msg = err?.response?.data?.error || 'Tambor não encontrado para o código informado.';
      setError(msg);
      showErrorMessage(msg);
    }
  };

  const reset = () => {
    setDrum(null);
    setError(null);
  };

  const formatDate = (v) => v ? new Date(v).toLocaleString('pt-BR') : '—';

  const flowLabel = flow === 'conference'
    ? 'Conferência de Compra'
    : flow === 'process'
      ? 'Processamento'
      : 'Leitura do Tambor';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 720, margin: '0 auto' }}>
      {isOperator && <BackToHome color={flowColor || COLORS.PRIMARY} />}
      <Card>
        <Title level={3} style={{ textAlign: 'center', marginBottom: 24, color: flowColor }}>{flowLabel}</Title>
        <Text type="secondary" style={{ display: 'block', textAlign: 'center', marginBottom: 16 }}>
          Aponte a câmera para o QR code do tambor ou digite o código manualmente.
        </Text>

        {!drum && !error && <QrScanner onScan={handleScan} />}
      </Card>

      {error && (
        <Card>
          <Result
            status="warning"
            title="Não foi possível localizar o tambor"
            subTitle={error}
            extra={[
              <Button key="retry" type="primary" icon={<ReloadOutlined />} onClick={reset} style={flowColor ? { background: flowColor, borderColor: flowColor } : undefined}>
                Tentar novamente
              </Button>
            ]}
          />
        </Card>
      )}

      {drum && (
        <Card title={`Tambor ${drum.code}`}>
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Cliente">{drum.client?.company_name || drum.client?.nickname || '—'}</Descriptions.Item>
            <Descriptions.Item label="Lote">#{drum.batch_id}</Descriptions.Item>
            <Descriptions.Item label="Status processamento">
              <Tag color={EDrumStatus.getByCode(drum.status)?.color}>
                {EDrumStatus.getByCode(drum.status)?.description}
              </Tag>
              {drum.processed_at && (
                <Text type="secondary"> por {drum.processedBy?.full_name || '—'} em {formatDate(drum.processed_at)}</Text>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Status conferência">
              <Tag color={EDrumPurchaseStatus.getByCode(drum.purchase_status)?.color}>
                {EDrumPurchaseStatus.getByCode(drum.purchase_status)?.description}
              </Tag>
              {drum.conferred_at && (
                <Text type="secondary"> por {drum.conferredBy?.full_name || '—'} em {formatDate(drum.conferred_at)}</Text>
              )}
            </Descriptions.Item>
          </Descriptions>

          <Alert
            style={{ marginTop: 16 }}
            type={drum.status === EDrumStatus.PROCESSED.code ? 'success' : 'info'}
            showIcon
            message={drum.status === EDrumStatus.PROCESSED.code
              ? 'Este tambor já foi processado. Abra a visualização para conferir os dados registrados.'
              : 'Este tambor ainda não foi processado.'}
          />

          <Space wrap style={{ marginTop: 16 }}>
            {/* Botões de re-fluxo só aparecem quando o tambor NÃO está no estado
                terminal daquele fluxo. Tambor já processado/conferido → view só-leitura. */}
            {canProcess && drum.status !== EDrumStatus.PROCESSED.code && (
              <Button
                type="primary"
                size="large"
                icon={<ExperimentOutlined />}
                onClick={() => navigate(`/drums/${drum.id}/process`, { state: { from: 'home', color: flowColor, machine: location.state?.machine } })}
                style={flowColor ? { background: flowColor, borderColor: flowColor } : undefined}
              >
                Ir para processamento
              </Button>
            )}
            {canConference && drum.purchase_status !== EDrumPurchaseStatus.CONFERRED.code && (
              <Button
                type="primary"
                size="large"
                icon={<ShoppingCartOutlined />}
                onClick={() => navigate(`/drums/${drum.id}/conference`, { state: { from: 'home', color: flowColor } })}
                style={flowColor ? { background: flowColor, borderColor: flowColor } : undefined}
              >
                Ir para conferência de compra
              </Button>
            )}
            {(drum.status === EDrumStatus.PROCESSED.code || drum.purchase_status === EDrumPurchaseStatus.CONFERRED.code) && (
              <Button
                size="large"
                onClick={() => navigate(`/drums/${drum.id}/view`, { state: { color: flowColor } })}
              >
                Visualizar tambor
              </Button>
            )}
            <Button size="large" icon={<ReloadOutlined />} onClick={reset}>
              Ler outro tambor
            </Button>
          </Space>
        </Card>
      )}
    </div>
  );
};

export default DrumEntry;
